import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../axiosinstance";
import "./PaymentReport.css";
import Pagination from "../Components/Pagination";
import { formatBadgeLabel, getPaymentModeBadgeClass } from "../helperFunctions";
import { TbCurrencyRupee } from "react-icons/tb";
import SearchableDropdown from "../Components/SearchableDropdown";

import { MdOutlinePayments } from "react-icons/md";

const QUICK_FILTER_OPTIONS = [
  { value: "last_7_days", label: "Last 7 days", type: "quick" },
  { value: "last_30_days", label: "Last 30 days", type: "quick" },
  { value: "last_90_days", label: "Last 90 days", type: "quick" },
  { value: "last_6_months", label: "Last 6 months", type: "quick" },
  { value: "custom", label: "Custom range", type: "custom" },
];

const FALLBACK_PERIOD_OPTION = QUICK_FILTER_OPTIONS[0];

const INR_CURRENCY = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const INR_NUMBER = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatINR(value, { symbol = true } = {}) {
  const num = Number(value ?? 0);
  if (!isFinite(num)) return symbol ? "Rs0.00" : "0.00";
  return (symbol ? INR_CURRENCY : INR_NUMBER).format(num);
}

function formatDate(dateValue) {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  return isNaN(d.getTime())
    ? dateValue
    : d.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function normalizeDateInput(value) {
  let normalizedValue = value;
  if (normalizedValue) {
    const [y, m, d] = normalizedValue.split("-");
    if (y && y.length > 4) {
      normalizedValue = `${y.slice(0, 4)}-${m || "01"}-${d || "01"}`;
    }
  }
  return normalizedValue;
}

function generateFilename(selectedPeriodOption, startDate, endDate) {
  const timestamp = new Date().toISOString().split("T")[0];

  if (selectedPeriodOption?.type === "custom") {
    return `payment_report_${startDate || "start"}_to_${endDate || "end"}_${timestamp}.xlsx`;
  }

  if (selectedPeriodOption?.type === "fy") {
    return `payment_report_${selectedPeriodOption.fy_label || "financial_year"}_${timestamp}.xlsx`;
  }

  return `payment_report_${selectedPeriodOption?.value || "report"}_${timestamp}.xlsx`;
}

function buildPaymentReportParams({
  selectedSubsidiary,
  selectedPeriodOption,
  customStartDate,
  customEndDate,
}) {
  const params = new URLSearchParams();
  const selectedOption = selectedPeriodOption || FALLBACK_PERIOD_OPTION;

  params.set("subsidiary_selected", selectedSubsidiary || "all");

  if (selectedOption.type === "fy") {
    if (selectedOption.fy_start)
      params.set("fy_start", selectedOption.fy_start);
    if (selectedOption.fy_end) params.set("fy_end", selectedOption.fy_end);
    return params;
  }

  if (selectedOption.type === "custom") {
    params.set("filter_type", "custom");
    if (customStartDate) params.set("start_date", customStartDate);
    if (customEndDate) params.set("end_date", customEndDate);
    return params;
  }

  params.set("filter_type", selectedOption.value);
  return params;
}

const PaymentReport = ({ isSidebarExpanded }) => {
  const [subsidiaries, setSubsidiaries] = useState([
    { subsidiary_id: "all", subsidiary_name: "All Subsidiaries" },
  ]);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState("all");

  const [, setFinancialYears] = useState([]);
  const [unifiedFilterOptions, setUnifiedFilterOptions] =
    useState(QUICK_FILTER_OPTIONS);
  const [selectedPeriodOption, setSelectedPeriodOption] = useState(
    FALLBACK_PERIOD_OPTION,
  );
  const [hasLoadedPeriodOptions, setHasLoadedPeriodOptions] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paymentData, setPaymentData] = useState([]);
  const [totalPaymentCount, setTotalPaymentCount] = useState(0);
  const [totalPaymentSum, setTotalPaymentSum] = useState("0.00");
  const [searchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    const fetchSubs = async () => {
      try {
        const res = await axiosInstance.get("/api/get_all_subsidiary", {
          params: { page_status: "Active" },
        });

        const raw = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];

        const list = raw
          .map((x) => ({
            subsidiary_id: x.subsidiary_id,
            subsidiary_name: x.Subsidiary_name,
          }))
          .filter((x) => x.subsidiary_id && x.subsidiary_name);

        const withAll = [
          { subsidiary_id: "all", subsidiary_name: "All Subsidiaries" },
          ...list,
        ];

        setSubsidiaries(withAll);
        if (!withAll.some((s) => s.subsidiary_id === selectedSubsidiary)) {
          setSelectedSubsidiary("all");
        }
      } catch (e) {
        console.error("Error fetching subsidiaries:", e);
        setSubsidiaries([
          { subsidiary_id: "all", subsidiary_name: "All Subsidiaries" },
        ]);
        setSelectedSubsidiary("all");
      }
    };

    fetchSubs();
  }, [selectedSubsidiary]);

  useEffect(() => {
    const fetchFinancialYears = async () => {
      try {
        const response = await axiosInstance.get("/api/financial-years");
        const list = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const fyOptions = list.map((financialYear) => ({
          label: `FY ${financialYear.fy_label}`,
          value: `fy_${financialYear.id}`,
          type: "fy",
          id: financialYear.id,
          fy_label: financialYear.fy_label,
          fy_start: financialYear.start_date,
          fy_end: financialYear.end_date,
          is_active: financialYear.is_active,
        }));

        const mergedOptions = [...fyOptions, ...QUICK_FILTER_OPTIONS];
        const activeFinancialYearOption =
          fyOptions.find((option) => option.is_active) ||
          fyOptions[0] ||
          FALLBACK_PERIOD_OPTION;

        setFinancialYears(list);
        setUnifiedFilterOptions(mergedOptions);
        setSelectedPeriodOption(activeFinancialYearOption);
      } catch (e) {
        console.error("Error fetching financial years:", e);
        setFinancialYears([]);
        setUnifiedFilterOptions(QUICK_FILTER_OPTIONS);
        setSelectedPeriodOption(FALLBACK_PERIOD_OPTION);
      } finally {
        setHasLoadedPeriodOptions(true);
      }
    };

    fetchFinancialYears();
  }, []);

  const filterQueryParams = useMemo(() => {
    return buildPaymentReportParams({
      selectedSubsidiary,
      selectedPeriodOption,
      customStartDate,
      customEndDate,
    });
  }, [
    selectedSubsidiary,
    selectedPeriodOption,
    customStartDate,
    customEndDate,
  ]);

  const listQueryParams = useMemo(() => {
    const params = new URLSearchParams(filterQueryParams);
    params.set("page", String(currentPage));
    params.set("per_page", String(perPage));
    return params.toString();
  }, [filterQueryParams, currentPage, perPage]);

  const fetchPaymentData = async () => {
    const selectedOption = selectedPeriodOption || FALLBACK_PERIOD_OPTION;

    if (
      selectedOption.type === "custom" &&
      (!customStartDate || !customEndDate)
    ) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axiosInstance.get(
        `/api/payment_report?${listQueryParams}`,
      );
      const data = res.data;
      const pagination = data.pagination || {};
      const paymentList = (data.payment_data || []).map((record) => ({
        srNo: record.sr_no || "",
        paymentId: record.payment_id || "",
        amount: record.amount || "0.00",
        chequeDate: record.cheque_date || "",
        chequeNumber: record.cheque_number || "",
        clientPan: record.client_pan || "",
        clientCompanyName: record.client_company_name || "",
        clientName: record.client_name || "",
        paymentDate: record.payment_date || "",
        paymentMode: record.payment_mode || "",
        transactionId: record.transaction_id || "",
      }));

      setPaymentData(paymentList);
      setTotalPaymentCount(data.number_of_payment ?? paymentList.length);
      setTotalPaymentSum(data.total_payment_sum ?? "0.00");
      setCurrentPage(pagination.page || currentPage);
      setPerPage(pagination.per_page || perPage);
      setTotalPages(pagination.total_pages || 0);
      setTotalRecords(pagination.total_records || paymentList.length);
    } catch (err) {
      setError(`Failed to load payment data: ${err.message}`);
      setPaymentData([]);
      setTotalPaymentCount(0);
      setTotalPaymentSum("0.00");
      setTotalPages(0);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedSubsidiary,
    selectedPeriodOption?.value,
    customStartDate,
    customEndDate,
  ]);

  useEffect(() => {
    if (!hasLoadedPeriodOptions) return;
    if (!selectedPeriodOption) return;

    if (selectedPeriodOption.type === "custom") {
      if (!customStartDate || !customEndDate) return;
    }

    fetchPaymentData();
  }, [
    selectedSubsidiary,
    selectedPeriodOption,
    customStartDate,
    customEndDate,
    hasLoadedPeriodOptions,
    currentPage,
    perPage,
  ]);

  const filteredPayments = useMemo(() => {
    const searchTerm = searchQuery.trim().toLowerCase();
    if (!searchTerm) return paymentData;
    return paymentData.filter((p) =>
      [
        p.clientPan,
        p.clientCompanyName,
        p.clientName,
        p.paymentMode,
        p.chequeNumber,
        p.transactionId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(searchTerm),
    );
  }, [paymentData, searchQuery]);

  const handlePeriodChange = (option) => {
    const nextSelectedOption = option || FALLBACK_PERIOD_OPTION;

    setSelectedPeriodOption(nextSelectedOption);
    setError("");

    if (nextSelectedOption.type !== "custom") {
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  const handleExport = async () => {
    const selectedOption = selectedPeriodOption || FALLBACK_PERIOD_OPTION;

    if (
      selectedOption.type === "custom" &&
      (!customStartDate || !customEndDate)
    ) {
      setError("Please select both start date and end date.");
      return;
    }

    try {
      const res = await axiosInstance.get(
        `/api/payment/export_excel?${filterQueryParams.toString()}`,
        { responseType: "blob" },
      );

      const blob = new Blob([res.data], {
        type:
          res.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = generateFilename(
        selectedOption,
        customStartDate,
        customEndDate,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => window.URL.revokeObjectURL(url), 500);
    } catch (err) {
      console.error("Export failed:", err);
      setError(`Export failed: ${err.message}`);
    }
  };

  const handlePaginationChange = (page, nextPerPage) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }

    if (nextPerPage !== perPage) {
      setPerPage(nextPerPage);
      setCurrentPage(1);
    }
  };

  return (
    <div
      className={`payment-report-container ${
        isSidebarExpanded ? "sidebar-expanded" : "sidebar-collapsed"
      }`}
    >
      <div className="payment-report-content">
        <div className="table-header">
          <div className="table-header-content">
            {/* LEFT SIDE */}
            <div className="table-left-side">
              <h2 className="jost-bold">Payment Report</h2>
            </div>

            {/* RIGHT SIDE */}
            <div className="table-right-side">
              {/* Subsidiary Filter */}
              <SearchableDropdown
                className="report-filter-dropdown"
                triggerClassName="filter-select"
                value={selectedSubsidiary}
                options={subsidiaries}
                getOptionValue={(option) => option.subsidiary_id}
                getOptionLabel={(option) => option.subsidiary_name}
                onChange={(option) =>
                  setSelectedSubsidiary(option?.subsidiary_id || "all")
                }
                placeholder="Select Entity"
                searchPlaceholder="Search subsidiary"
              />

              {/* Period Filter */}
              <SearchableDropdown
                className="report-filter-dropdown period-select"
                triggerClassName="filter-select period-select"
                value={selectedPeriodOption?.value || ""}
                options={unifiedFilterOptions}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={handlePeriodChange}
                placeholder="Select Period"
                searchPlaceholder="Search period"
              />

              {/* Custom Date Range */}
              {selectedPeriodOption?.type === "custom" && (
                <div className="custom-date-range">
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => {
                      setCustomStartDate(normalizeDateInput(e.target.value));
                      setError("");
                    }}
                    className="date-input"
                  />

                  <span className="date-separator">to</span>

                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => {
                      setCustomEndDate(normalizeDateInput(e.target.value));
                      setError("");
                    }}
                    className="date-input"
                  />
                </div>
              )}

              {/* Export Button */}
              <button onClick={handleExport} className="export-button">
                Export Excel
              </button>
            </div>
          </div>
        </div>

        {error && <div className="loading-message">{error}</div>}

        <div className="dashboard payment-summary">
          <div className="cards-client payment-summary-cards">
            <div className="card-client payment-summary-card payments-count">
              <span className="ledger-summary-icon" aria-hidden="true">
                <MdOutlinePayments />
              </span>
              <div className="ledger-summary-copy">
                <span className="summary-label">Total Payments</span>
                <span className="summary-value">{totalPaymentCount}</span>
              </div>
            </div>
            <div className="card-client payment-summary-card total-amount">
              <span className="ledger-summary-icon" aria-hidden="true">
                {/* <TbCurrencyRupee /> */}
                <TbCurrencyRupee />
              </span>
              <div className="ledger-summary-copy">
                <span className="summary-label">Total Amount</span>
                <span className="summary-value">
                  {formatINR(totalPaymentSum)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-message">Loading payment data...</div>
        ) : (
          <div className="payment-report-table-panel">
            <div className="data-list-scroll-body payment-report-scroll-body">
              {/* <div className="data-list-table"> */}
              <table className="table multilevel-data">
                <thead className="sticky-header">
                  <tr className="table-row">
                    <th className="center-align">Sr. No</th>
                    <th className="center-align">Payment Date</th>
                    <th>
                      <span
                        style={{
                          textAlign: "left",
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                        }}
                      >
                        Client Pan
                      </span>
                    </th>
                    <th>
                      <span
                        style={{
                          textAlign: "left",
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                        }}
                      >
                        Client Name
                      </span>
                    </th>
                    <th>
                      <span
                        style={{
                          textAlign: "left",
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                        }}
                      >
                        Client Company Name
                      </span>
                    </th>
                    <th>
                      <span
                        style={{
                          textAlign: "left",
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                        }}
                      >
                        Payment Mode
                      </span>
                    </th>
                    <th className="center-align">Amount</th>
                    <th className="center-align">Cheque Number </th>
                    <th className="center-align">Cheque Date</th>
                    <th className="center-align">Transaction Id</th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: "#fff" }}>
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="no-data">
                        No payment reports found
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((payment, index) => (
                      <tr
                        key={payment.paymentId || index}
                        className="table-row hoverable-row sub-td"
                      >
                        <td className="center-align">{payment.srNo || "-"}</td>
                        <td className="center-align">
                          {formatDate(payment.paymentDate)}
                        </td>
                        <td>{payment.clientPan || "-"}</td>
                        <td>
                          {payment.clientName
                            ? payment.clientName
                                .toLowerCase()
                                .split(" ")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1),
                                )
                                .join(" ")
                            : "-"}
                        </td>
                        <td>
                          {payment.clientCompanyName
                            ? payment.clientCompanyName
                                .toLowerCase()
                                .split(" ")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1),
                                )
                                .join(" ")
                            : "-"}
                        </td>
                        <td className="payment-mode-cell">
                          <span
                            className={`value-badge ${getPaymentModeBadgeClass(
                              payment.paymentMode,
                            )}`}
                          >
                            {formatBadgeLabel(payment.paymentMode, "-")}
                          </span>
                        </td>
                        <td className="center-align amount-column price-left-align">
                          {formatINR(payment.amount)}
                        </td>
                        <td className="center-align">
                          {payment.chequeNumber || "-"}
                        </td>
                        <td className="center-align">
                          {formatDate(payment.chequeDate)}
                        </td>
                        <td className="center-align transaction-id">
                          {payment.transactionId || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {/* </div> */}
            </div>
            <Pagination
              className="payment-report-pagination"
              showPerPage={perPage}
              total={totalRecords}
              currentPage={currentPage}
              itemsPerPage={perPage}
              totalPages={totalPages}
              onPaginationChange={handlePaginationChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReport;
